import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { Game } from "@/game/engine";
import { Sfx } from "@/game/audio";
import { Music } from "@/game/music";
import { Snapshot } from "@/game/types";
import { DESKTOP_DIMS, Dims, MOBILE_DIMS } from "@/game/level";
import { themeFor } from "@/game/renderer";
import { BoardSize, CONTROL_MODES, ControlMode, StickSize, isTouchDevice, loadBoard, loadControls, loadPackCode, loadSkin, loadStickSize, loadUnlocked, markInstallNudged, markInstalled, platformKey, playerId, recordDailyPlay, saveBoard, saveControls, saveStickSize, shouldNudgeInstall, todayKey } from "@/game/storage";

const RATE_URL = "https://play.google.com/store/apps/details?id=com.rollpuproll.cavernescape";
const RATE_KEY = "cave-critter-rate-day";
const maybeAskRating = () => {
  if (localStorage.getItem(RATE_KEY) === todayKey()) return;
  localStorage.setItem(RATE_KEY, todayKey());
  setTimeout(() => toast(t("rateTitle"), {
    description: t("rateText"),
    duration: 9000,
    action: { label: t("rateBtn"), onClick: () => window.open(RATE_URL, "_blank", "noopener") },
    cancel: { label: t("rateLater"), onClick: () => {} },
  }), 1400);
};
const SEEN_KEY = "cave-critter-pack-seen";
import { reportPack } from "@/game/api";
import { PackModal, usePack } from "@/components/Pack";
import { preloadSprites, Skin } from "@/game/sprites";
import { GameCanvas } from "@/components/GameCanvas";
import { DPad, PushButton } from "@/components/DPad";
import { Joystick } from "@/components/Joystick";
import { TrailerCaptions, TrailerSoundHint } from "@/components/Trailer";
import { createBot } from "@/game/bot";

const TRAILER = new URLSearchParams(window.location.search).has("trailer");
const TRAILER_NO_BOT = new URLSearchParams(window.location.search).get("trailer") === "manual";
import { Hud, HudActions } from "@/components/Hud";
import { Ending, DailyOver, GameOver, HowToPlay, InstallHelp, InstallNudge, LeaderboardModal, LevelClear, LevelSelect, Paused, StartScreen } from "@/components/Overlays";
import { IntroScene } from "@/components/IntroScene";
import { Breather } from "@/components/Breather";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { BREATHER_MS } from "@/game/engine";
import { t, useLang } from "@/game/i18n";

const SMALL_DIMS: Dims = { cols: 9, rows: 9 };
const TALL_DIMS: Dims = { cols: 11, rows: 15 };
const WIDE_DIMS: Dims = { cols: 15, rows: 11 };
const isDesktop = () => window.innerWidth >= 900 && !isTouchDevice();
const desktopDims = (): Dims => {
  const cell = (window.innerHeight - 36 - 44) / DESKTOP_DIMS.rows;
  const cols = Math.min(24, Math.max(DESKTOP_DIMS.cols, Math.floor((window.innerWidth - 32) / cell)));
  return { cols, rows: DESKTOP_DIMS.rows };
};
const pickDims = (board: BoardSize): Dims => {
  if (isDesktop()) return desktopDims();
  if (board === "9") return SMALL_DIMS;
  if (board === "tall") return window.innerWidth > window.innerHeight ? WIDE_DIMS : TALL_DIMS;
  return MOBILE_DIMS;
};

const HUD_H = 36;
const PORTRAIT_CONTROLS_H = 214;
const PORTRAIT_ACTIONS_H = 46;
const SIDE_CONTROLS_W = 192;

function useCanvasSize(dims: Dims, dpad: boolean, controlsH = PORTRAIT_CONTROLS_H) {
  const calc = () => {
    const w = window.innerWidth, h = window.innerHeight;
    const landscape = w > h;
    const desktop = w >= 900 && !isTouchDevice();
    let availW: number, availH: number;
    if (landscape) {
      availW = w - (dpad ? SIDE_CONTROLS_W * 2 : 32);
      availH = h - HUD_H - (desktop ? 44 : 8);
    } else {
      availW = w - 8;
      availH = h - HUD_H - PORTRAIT_ACTIONS_H - (dpad ? controlsH : 64);
    }
    const cell = Math.max(16, Math.min(availW / dims.cols, availH / dims.rows, desktop ? Infinity : 64));
    const width = Math.floor(cell * dims.cols), height = Math.floor(cell * dims.rows);
    return { width, height, landscape, desktop };
  };
  const [size, setSize] = useState(calc);
  useEffect(() => {
    const onResize = () => setSize(calc());
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("orientationchange", onResize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims.cols, dims.rows, dpad, controlsH]);
  return size;
}

const fsDoc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
const fsSupported = () => !!(document.fullscreenEnabled || (document.documentElement as any).webkitRequestFullscreen);
const exitFullscreenSafe = () => { if (document.fullscreenElement || fsDoc.webkitFullscreenElement) (document.exitFullscreen?.bind(document) ?? fsDoc.webkitExitFullscreen?.bind(document))?.(); };

const THEME_SHELL: Record<number, [string, string]> = {
  1: ["#7a2a10", "#3a1206"],
  2: ["#123a6b", "#071a33"],
  3: ["#5a1a05", "#2a0a02"],
  4: ["#2f5a10", "#132a06"],
  5: ["#2a5f8f", "#0f2a45"],
  6: ["#4a1a6b", "#1e0a30"],
};



export default function App() {
  const [boardSize, setBoardSize] = useState<BoardSize>(loadBoard);
  const [skin, setSkin] = useState<Skin>(loadSkin() as Skin);
  const boardRef = useRef(boardSize);
  boardRef.current = boardSize;
  const game = useMemo(() => { const g = new Game(pickDims(loadBoard())); g.dimsFn = () => pickDims(boardRef.current); return g; }, []);
  const sfx = useRef(new Sfx());
  const musicRef = useRef(new Music());
  const [snap, setSnap] = useState<Snapshot>(() => game.snapshot());
  const [muted, setMuted] = useState(sfx.current.muted);
  const [music, setMusic] = useState(musicRef.current.enabled);
  const [sfxVol, setSfxVol] = useState(sfx.current.volume);
  const [musicVol, setMusicVol] = useState(musicRef.current.volume);
  const volumes = { sfx: sfxVol, music: musicVol, onSfx: (v: number) => { sfx.current.setVolume(v); setSfxVol(v); }, onMusic: (v: number) => { musicRef.current.setVolume(v); setMusicVol(v); } };
  const onPup = useCallback(() => sfx.current.play("crush"), []);
  const [controls, setControls] = useState<ControlMode>(loadControls);
  const dpad = controls !== "swipe";
  const setMode = useCallback((m: ControlMode) => { saveControls(m); setControls(m); toast(t(m === "dpad" ? "ctrlDpad" : m === "joystick" ? "ctrlJoystick" : "ctrlSwipe"), { duration: 1500 }); }, []);
  const Stick = controls === "joystick" ? Joystick : DPad;
  const [stick, setStick] = useState<StickSize>(loadStickSize);
  const setStickSize = useCallback((s: StickSize) => { saveStickSize(s); setStick(s); }, []);
  const bigStick = controls === "joystick" && stick === "l";
  const [trailerSound, setTrailerSound] = useState(false);
  const [help, setHelp] = useState(false);
  const [levels, setLevels] = useState(false);
  const [board, setBoard] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  const [packJoin, setPackJoin] = useState<string | null>(() => { const c = new URLSearchParams(window.location.search).get("copor"); return c ? c.toUpperCase().replace("-", "") : null; });
  const [packKey, setPackKey] = useState(0);
  const packCode = loadPackCode();
  const pack = usePack(packKey);
  useEffect(() => { if (packJoin) setPackOpen(true); }, [packJoin]);
  useEffect(() => { if (snap.phase === "gameover" && snap.daily) maybeAskRating(); }, [snap.phase, snap.daily]);
  useEffect(() => {
    if (!pack) return;
    const me = playerId();
    const mine = pack.members.find((m) => m.player_id === me);
    let seen: Record<string, { best_score: number; daily_score: number }> = {};
    try { seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}"); } catch { seen = {}; }
    const first = Object.keys(seen).length === 0;
    const next: typeof seen = {};
    for (const m of pack.members) {
      next[m.player_id] = { best_score: m.best_score, daily_score: m.daily_score };
      if (first || !mine || m.player_id === me) continue;
      const prev = seen[m.player_id];
      if (m.best_score > (prev?.best_score ?? 0) && m.best_score > mine.best_score) toast(t("packBeat", { n: m.name, s: m.best_score }), { duration: 6000 });
      else if (m.daily_score > (prev?.daily_score ?? 0) && m.daily_score > mine.daily_score) toast(t("packBeatDaily", { n: m.name, s: m.daily_score }), { duration: 6000 });
    }
    localStorage.setItem(SEEN_KEY, JSON.stringify(next));
  }, [pack]);
  const [breather, setBreather] = useState(false);
  useEffect(() => {
    if (!snap.breather) return;
    setBreather(true);
    const id = setTimeout(() => setBreather(false), BREATHER_MS);
    return () => clearTimeout(id);
  }, [snap.breather, snap.level]);
  const onPant = useCallback(() => sfx.current.pant(6), []);
  useEffect(() => {
    if (snap.phase !== "gameover" && snap.phase !== "ending") return;
    const code = loadPackCode();
    if (!code) return;
    reportPack(code, playerId(), snap.daily ? "daily" : "classic", snap.score, snap.daily ? snap.wave : snap.level).then(() => setPackKey((k) => k + 1)).catch(() => {});
  }, [snap.phase, snap.daily, snap.score, snap.level, snap.wave]);
  const [fullscreen, setFullscreen] = useState<boolean | null>(() => (fsSupported() ? false : null));
  const dims = snap.phase === "start" ? pickDims(boardSize) : game.dims;
  const { width, height, landscape, desktop } = useCanvasSize(dims, dpad, bigStick ? PORTRAIT_CONTROLS_H + 50 : PORTRAIT_CONTROLS_H);
  useEffect(() => { if (boardSize === "tall" && !isDesktop()) game.transposeForOrientation(landscape); }, [landscape, boardSize, game]);
  useEffect(() => { preloadSprites(skin); }, [skin]);
  useEffect(() => {
    if (!TRAILER) return;
    if (!game.god) game.toggleGod();
    game.startAt(3);
    const m = musicRef.current;
    m.enabled = true;
    const kick = () => { m.start(); m.resume(); setTimeout(() => setTrailerSound(m.running), 300); };
    window.addEventListener("pointerdown", kick);
    window.addEventListener("keydown", kick);
    const bot = createBot(game);
    let raf = 0;
    const loop = () => {
      const st = game.state;
      if (st.phase === "playing") { if (!TRAILER_NO_BOT) bot.step(); }
      else if (st.phase === "levelclear") { if (st.bonus) { st.bonusResult = "fail"; } game.nextLevel(); }
      else if (st.phase === "gameover" || st.phase === "ending") game.startAt(3);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("pointerdown", kick); window.removeEventListener("keydown", kick); };
  }, [game]);

  useEffect(() => {
    (window as unknown as { __game: Game }).__game = game;
    game.onEvent = (e) => {
      sfx.current.play(e);
      const vib = navigator.vibrate?.bind(navigator);
      if (!vib) return;
      if (e === "crush" || e === "eat") vib(40);
      else if (e === "death") { vib([90, 50, 140]); if (!game.state.daily && !game.state.bonus) maybeAskRating(); }
      else if (e === "extra" || e === "power") vib([30, 30, 30, 30, 60]);
    };
    return game.subscribe(setSnap);
  }, [game]);

  const [introScene, setIntroScene] = useState(false);
  useEffect(() => {
    if (snap.phase === "playing" || introScene) musicRef.current.start();
  }, [snap.phase, introScene]);
  useEffect(() => {
    musicRef.current.setMode(TRAILER ? "trailer" : introScene ? "intro" : snap.bonus && snap.phase === "playing" ? "tunnel" : "cave");
  }, [snap.bonus, snap.phase, introScene]);
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState !== "hidden") return;
      if (game.state.phase === "playing") game.togglePause();
      musicRef.current.stop();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", onHide); };
  }, [game]);

  useEffect(() => {
    const onFs = () => setFullscreen(!!(document.fullscreenElement || fsDoc.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => { document.removeEventListener("fullscreenchange", onFs); document.removeEventListener("webkitfullscreenchange", onFs); };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
    if (document.fullscreenElement || fsDoc.webkitFullscreenElement) {
      (document.exitFullscreen?.bind(document) ?? fsDoc.webkitExitFullscreen?.bind(document))?.();
    } else {
      (el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el))?.()?.catch?.(() => {});
    }
  }, []);


  const enterFullscreen = useCallback(() => {
    if (!fsSupported() || document.fullscreenElement || fsDoc.webkitFullscreenElement) return;
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: (o?: FullscreenOptions) => Promise<void> };
    (el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el))?.({ navigationUI: "hide" })?.catch?.(() => {});
  }, []);

  const togglePause = useCallback(() => game.togglePause(), [game]);
  const startGame = useCallback(() => { enterFullscreen(); setIntroScene(true); }, [enterFullscreen]);
  const finishIntro = useCallback(() => { setIntroScene(false); game.start(); }, [game]);
  const startDaily = useCallback(() => { enterFullscreen(); recordDailyPlay(); game.startDaily(todayKey()); }, [enterFullscreen, game]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Enter" && e.code !== "Space") return;
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "BUTTON") return;
      if (snap.phase === "playing" && e.code === "Space") return;
      if (snap.phase === "start") { if (introScene) finishIntro(); else startGame(); }
      else if (snap.phase === "levelclear") game.nextLevel();
      else if (snap.phase === "gameover" || snap.phase === "ending") game.restart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [game, snap.phase, introScene, finishIntro, startGame]);

  const [installEvt, setInstallEvt] = useState<(Event & { prompt: () => Promise<void> }) | null>(null);
  const [installHelp, setInstallHelp] = useState(false);
  const installed = useMemo(() => window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: fullscreen)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true, []);
  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setInstallEvt(e as Event & { prompt: () => Promise<void> }); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const onInstall = installed ? undefined : installEvt ? () => { installEvt.prompt(); setInstallEvt(null); } : () => setInstallHelp(true);
  const [installNudge, setInstallNudge] = useState(false);
  useEffect(() => {
    if (installed) markInstalled();
    const onInstalled = () => markInstalled();
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [installed]);
  const quitGame = useCallback(() => {
    game.quit();
    exitFullscreenSafe();
    if (!installed && !TRAILER && shouldNudgeInstall()) { markInstallNudged(); setInstallNudge(true); }
  }, [game, installed]);

  const lang = useLang();
  const playing = snap.phase !== "start";
  const [shellA, shellB] = THEME_SHELL[playing ? themeFor(snap.level) : 1];
  const [intro, setIntro] = useState<number | null>(null);
  const introKey = useRef("");
  useEffect(() => {
    if (snap.phase !== "playing") { if (snap.phase !== "paused") introKey.current = ""; return; }
    const k = `${snap.level}-${snap.bonus}`;
    if (introKey.current === k) return;
    introKey.current = k;
    setIntro(snap.level);
    const t = setTimeout(() => setIntro(null), snap.bonus ? 2800 : 2200);
    return () => clearTimeout(t);
  }, [snap.level, snap.bonus, snap.phase === "playing"]);
  const [countdown, setCountdown] = useState(3);
  useEffect(() => {
    if (snap.phase !== "levelclear") return;
    setCountdown(3);
    const iv = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    const t = setTimeout(() => game.nextLevel(), 3000);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [snap.phase, game]);

  const actions = (
    <HudActions
      phase={snap.phase}
      muted={muted}
      music={music}
      dpad={dpad}
      fullscreen={fullscreen}
      onMute={() => { const v = !muted; sfx.current.setMuted(v); setMuted(v); }}
      onMusic={() => { const v = !music; musicRef.current.setEnabled(v); setMusic(v); }}
      onDpad={() => setMode(CONTROL_MODES[(CONTROL_MODES.indexOf(controls) + 1) % CONTROL_MODES.length])}
      onPause={togglePause}
      onFullscreen={toggleFullscreen}
      onQuit={quitGame}
    />
  );

  return (
    <div key={lang} data-testid="app-root" className={`app-shell ${playing ? "app-shell--playing" : ""} ${TRAILER ? "is-trailer" : ""}`} style={{ "--shell-a": shellA, "--shell-b": shellB } as React.CSSProperties}>
      <div className="ambient-glow" />
      {!playing && <StartScreen best={snap.best} onPlay={startGame} onDaily={startDaily} onHelp={() => setHelp(true)} onBoard={() => setBoard(true)} onLevels={() => setLevels(true)} onPack={() => setPackOpen(true)} onPup={onPup} packName={pack?.name ?? (packCode ? undefined : null)} onInstall={onInstall} boardSize={isDesktop() ? undefined : boardSize} onBoardSize={(b) => { saveBoard(b); setBoardSize(b); }} skin={skin} onSkin={setSkin} />}
      {playing && (
        <div data-testid="game-screen" className={`game-screen ${landscape ? "is-landscape" : "is-portrait"} ${dpad ? "has-dpad" : ""} ${bigStick ? "stick-l" : ""}`}>
          <Hud snap={snap} actions={landscape ? actions : undefined} onSecret={() => game.toggleGod()} />
          <div className="stage" data-testid="stage">
            <div className="stage-inner" style={{ width }}>
              <GameCanvas game={game} width={width} height={height} onPauseKey={togglePause} />
              {!landscape && <div className="portrait-actions">{actions}</div>}
              {intro !== null && snap.phase === "playing" && (
                <div data-testid="level-intro" className="level-intro" style={{ width }}>
                  <div className="level-intro-card">
                    <span className="eyebrow">{snap.daily ? t("daily") : snap.bonus ? t("secretPassage") : t("levelIntro", { n: intro })}</span>
                    <span className="level-intro-title">{snap.daily ? t("dailyIntro") : snap.bonus ? t("findExit") : t("themes")[themeFor(intro) - 1]}</span>
                    {snap.bonus && <span className="text-xs text-white/80 mt-1">{t("successSkips", { n: snap.level + 1 })}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
          {!landscape && (
            <div data-testid="controls-portrait" className="controls-portrait">
              {dpad && <Stick onHold={(d) => game.setHeld(d)} size={stick} />}
              <div className="controls-mid">
                {!dpad && <span className="hud-hint">{t("swipeHint")}</span>}
              </div>
              {dpad && <PushButton onPush={() => game.pushAction()} />}
            </div>
          )}
          {landscape && dpad && (
            <>
              <div data-testid="controls-left" className="ctrl-fixed ctrl-left"><Stick onHold={(d) => game.setHeld(d)} size={stick} /></div>
              <div data-testid="controls-right" className="ctrl-fixed ctrl-right"><PushButton onPush={() => game.pushAction()} /></div>
            </>
          )}
          {landscape && desktop && (
            <span data-testid="desktop-hint" className="hud-hint desktop-hint">{t("desktopHint")}</span>
          )}
        </div>
      )}
      {help && <HowToPlay onClose={() => setHelp(false)} />}
      {introScene && <IntroScene onDone={finishIntro} />}
      {installHelp && <InstallHelp onClose={() => setInstallHelp(false)} />}
      {installNudge && <InstallNudge onYes={() => { setInstallNudge(false); onInstall?.(); }} onLater={() => setInstallNudge(false)} />}
      {levels && <LevelSelect unlocked={loadUnlocked()} dims={dims} onPick={(lv) => { setLevels(false); enterFullscreen(); game.startAt(lv); }} onClose={() => setLevels(false)} />}
      {board && <LeaderboardModal onClose={() => setBoard(false)} platform={platformKey()} />}
      {breather && snap.phase === "playing" && <Breather skin={skin} onPant={onPant} />}
      {createPortal(<Toaster position="top-center" richColors closeButton />, document.body)}
      {TRAILER && playing && <TrailerCaptions />}
      {TRAILER && !trailerSound && <TrailerSoundHint />}
      {packOpen && <PackModal joinCode={packJoin} onClose={() => { setPackOpen(false); setPackJoin(null); setPackKey((k) => k + 1); }} />}
      {snap.phase === "levelclear" && <LevelClear snap={snap} onNext={() => game.nextLevel()} countdown={countdown} />}
      {snap.phase === "gameover" && !snap.daily && <GameOver snap={snap} onRestart={() => game.restart()} onRetry={() => game.startAt(game.retryLevel())} onQuit={quitGame} packKey={packKey} />}
      {snap.phase === "gameover" && snap.daily && <DailyOver snap={snap} onRestart={() => game.restart()} onQuit={quitGame} platform={platformKey()} packKey={packKey} />}
      {snap.phase === "ending" && <Ending snap={snap} onRestart={() => game.restart()} onBoard={() => setBoard(true)} />}
      {snap.phase === "paused" && <Paused onResume={togglePause} volumes={volumes} controls={desktop ? undefined : { mode: controls, onChange: setMode, stick, onStick: setStickSize }} />}
    </div>
  );
}
